import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import "./RegistrationPage.less";
import "./ChangePasswordPage.less";
import RowContainer from "../../common/RowContainer";
import { useChangePasswordMutation } from "../../../redux/features/auth/authApi";
import { StyledPaddingColumnContainer } from "../../common/ColumnContainer";

import { useFormik } from "formik";
import * as Yup from "yup";
import { FormattedMessage, useIntl } from "react-intl";
import ModalComponent from "../../common/ModalComponent";
import { useNavigate } from "react-router-dom";
import { commonRoutes } from "../../../utils/routes/Routes";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { selectAuthData } from "../../../redux/features/auth/authSlice";
import ChangePasswordThankYou from "./ChangePasswordThankYou";

const ChangePasswordPage = () => {
  //INIT
  const theme = useTheme();
  const navigate = useNavigate();
  const intl = useIntl();

  //REDUX
  const [changePassword, { isLoading, isError, isSuccess }] =
    useChangePasswordMutation();

  const auth = useSelector(selectAuthData);

  //STATE
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
    slaveCode: "",
  });

  const [showPassword, setShowPassword] = useState([
    { id: "newPassword", show: false },
    { id: "confirmPassword", show: false },
  ]);

  //FUNCTIONS
  const handleChangePasswordSubmit = async (values) => {
    try {
      await changePassword({
        token: auth.token,
        changePasswordData: {
          password: values.newPassword,
          slave_code: values.slaveCode,
        },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleCloseAndReset = () => {
    navigate(commonRoutes.login);
  };

  const handleClickShowPassword = (id) => {
    setShowPassword((prevState) => {
      return prevState.map((passwordField) =>
        passwordField.id === id
          ? { ...passwordField, show: !passwordField.show }
          : passwordField
      );
    });
  };

  //FORMIK
  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        intl.formatMessage({ id: "password.requirements" })
      )

      .min(8, <FormattedMessage id="password.minimum" />)
      .required(<FormattedMessage id="register.form.warnings.empty" />),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref("newPassword"), null],
      <FormattedMessage id="password.match" />
    ),
    slaveCode: Yup.string().min(
      6,
      <FormattedMessage id="register.form.warnings.field" />
    ),
  });

  const formik = useFormik({
    initialValues: formData,
    validationSchema: validationSchema,
    onSubmit: handleChangePasswordSubmit,
  });

  const formItems = [
    {
      label: <FormattedMessage id="profile.createNewPassword" />,
      type: showPassword.find((item) => item.id === "newPassword").show
        ? "text"
        : "password",
      name: "newPassword",
      xs: 12,
      sm: 12,
    },
    {
      label: <FormattedMessage id="profile.confirmNewPassword" />,
      type: showPassword.find((item) => item.id === "confirmPassword").show
        ? "text"
        : "password",
      name: "confirmPassword",
      xs: 12,
      sm: 12,
    },
  ];

  return (
    <div className="reset-pwd-page form-container">
      <RowContainer center>
        <Typography variant="h5">Reset Password</Typography>
      </RowContainer>
      <RowContainer center>
        <Typography variant="p" component="p" textAlign={"center"}>
          <FormattedMessage id="password.requirements" />
        </Typography>
      </RowContainer>

      <form onSubmit={formik.handleSubmit}>
        <StyledPaddingColumnContainer
          align="center"
          center
          style={{ gap: "3rem" }}
          className="reset-pwd-form"
        >
          <Grid
            container
            spacing={3}
            rowSpacing={8}
            className="field-container"
          >
            {formItems.map((item, index) => (
              <Grid item xs={item.xs} sm={item.sm} height="fit-content">
                <TextField
                  size="small"
                  autoComplete="off"
                  required
                  fullWidth
                  label={item.label}
                  type={item.type}
                  name={item.name}
                  value={formik.values[item.name]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched[item.name] &&
                    Boolean(formik.errors[item.name])
                  }
                  helperText={
                    formik.touched[item.name] && formik.errors[item.name]
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => handleClickShowPassword(item.name)}
                          onMouseDown={() => handleClickShowPassword(item.name)}
                        >
                          {showPassword.find(
                            (passwordField) => passwordField.id === item.name
                          ).show ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            ))}
            {auth?.userData?.is_slave ? (
              <Grid item xs={12} sm={12} height="fit-content">
                <TextField
                  size="small"
                  autoComplete="off"
                  required={auth?.userData?.isSlave}
                  fullWidth
                  label={"Inserisci il Codice Slave"}
                  type={"text"}
                  name={"slaveCode"}
                  value={formik.values["slaveCode"]}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched["slaveCode"] &&
                    Boolean(formik.errors["slaveCode"])
                  }
                  helperText={
                    formik.touched["slaveCode"] && formik.errors["slaveCode"]
                  }
                ></TextField>
              </Grid>
            ) : null}
          </Grid>
          <Grid item xs={12} sx={{ overflow: "hidden" }}>
            {isError ? (
              <Typography
                className="error-message"
                paragraph
                style={{
                  color: theme.palette.error.main,
                }}
              >
                <FormattedMessage id="password.error.reset" />
              </Typography>
            ) : null}
            <RowContainer center>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || !formik.isValid}
              >
                {isLoading ? (
                  <CircularProgress color="inherit" size={"30px"} />
                ) : (
                  <span>
                    <FormattedMessage id="button.send" />
                  </span>
                )}
              </Button>
            </RowContainer>
          </Grid>
        </StyledPaddingColumnContainer>
      </form>
      {isSuccess ? (
        <ModalComponent
          open={true}
          handleClose={handleCloseAndReset}
          minHeight="auto"
        >
          <ChangePasswordThankYou handleClose={handleCloseAndReset} />
        </ModalComponent>
      ) : null}
    </div>
  );
};

export default ChangePasswordPage;
